import { ThemedText } from "@/components/ThemedText";
import AlertModal from "@/components/ui/AlertModal.web";
import {
  FormButton,
  FormCard,
  FormPageContainer,
  RHFFormInput,
} from "@/components/ui/forms";
import { getErrorMessage } from "@/utils/errors";
import { zodResolver } from "@hookform/resolvers/zod";
import { Image } from "expo-image";
import Head from "expo-router/head";
import { StatusBar } from "expo-status-bar";
import * as FirebaseAuth from "firebase/auth";
import React, { useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { StyleSheet, useWindowDimensions, View } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import {
  forgotPasswordSchema,
  type ForgotPasswordFormValues,
} from "./forgot-password.schema";

const ForgotPasswordScreen = () => {
  const { width } = useWindowDimensions();
  const isLargeScreen = width >= 768;
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const [modalInfo, setModalInfo] = useState<{
    title: string;
    message: string;
    onClose?: () => void;
  } | null>(null);

  const { control, handleSubmit } = useForm<ForgotPasswordFormValues>({
    mode: "onTouched",
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: "" },
  });
  const watchedEmail = useWatch({ control, name: "email" });
  const hasRequiredFields = !!watchedEmail;

  const handlePasswordReset = async ({ email }: ForgotPasswordFormValues) => {
    setLoading(true);
    try {
      await FirebaseAuth.sendPasswordResetEmail(FirebaseAuth.getAuth(), email);
    } catch (error: any) {
      if (error.code !== "auth/user-not-found") {
        setModalInfo({ title: t("ERROR"), message: getErrorMessage(error) });
        setLoading(false);
        return;
      }
    }

    setModalInfo({ title: t("SUCCESS"), message: t("IF_ACCOUNT_EXISTS") });
    setLoading(false);
  };

  return (
    <SafeAreaView
      style={[
        styles.safeAreaViewContainer,
        { backgroundColor: isLargeScreen ? "#1e3a8a" : "#ffffff" },
      ]}
      edges={["top", "bottom"]}
    >
      <Head>
        <title>{t("PAGE_TITLE_FORGOT_PASSWORD")}</title>
      </Head>
      <StatusBar style={isLargeScreen ? "light" : "dark"} animated={true} />
      <FormPageContainer>
        <FormCard>
          <View className="items-center mb-6">
            <Image
              source={require("../../assets/images/vehilink-icon.webp")}
              style={{ width: 100, height: 100, borderRadius: 50 }}
              contentFit="cover"
              accessibilityLabel={t("VEHILINK_LOGO_ALT")}
            />
            <ThemedText className="mt-4 text-xl font-normal text-gray-900">
              {t("FORGOT_PASSWORD")}
            </ThemedText>
          </View>

          <RHFFormInput
            control={control}
            name="email"
            label={t("EMAIL")}
            placeholder={t("EMAIL_PLACEHOLDER")}
            autoCapitalize="none"
            keyboardType="email-address"
          />

          <FormButton
            onPress={handleSubmit(handlePasswordReset)}
            disabled={!hasRequiredFields || loading}
            text={loading ? t("SENDING") : t("RESET_PASSWORD")}
          />
        </FormCard>
      </FormPageContainer>
      <AlertModal
        visible={!!modalInfo}
        title={modalInfo?.title ?? ""}
        message={modalInfo?.message ?? ""}
        onClose={() => {
          modalInfo?.onClose?.();
          setModalInfo(null);
        }}
      />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeAreaViewContainer: { flex: 1 },
});

export default ForgotPasswordScreen;
